import { useEffect, useState } from "react";
import { Alert, Button, Stack } from "@mui/material";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * Slim install prompt — shown once after the browser fires beforeinstallprompt
 * and not dismissed-in-this-session. Hidden when already running standalone.
 */
export function PwaInstallBanner() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const dismissedKey = "im_pwa_install_dismissed_at";

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (sessionStorage.getItem(dismissedKey)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  if (!evt) return null;

  return (
    <Alert
      severity="info"
      sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1400, boxShadow: 4 }}
      action={
        <Stack direction="row" spacing={1}>
          <Button color="inherit" size="small" onClick={() => {
            sessionStorage.setItem(dismissedKey, new Date().toISOString());
            setEvt(null);
          }}>Later</Button>
          <Button variant="contained" color="primary" size="small"
            onClick={async () => {
              await evt.prompt();
              await evt.userChoice;
              setEvt(null);
            }}>Install app</Button>
        </Stack>
      }
    >
      Install HealInventory as a counter-app on this device for faster billing.
    </Alert>
  );
}
